import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Button, CardContent, CardTitle, Overlay, Image
} from '@thd-olt-component-react/core-ui';
import { clickEvent, impressionEvent } from '../publisher';
import '../PrivateLabelCreditCard.scss';
import { largeCardImage, DEFAULT_PREQUAL_URL } from '../constants';
import { loadEstimatorScript, getPrequalOfferLink, fetchDeclinePrequalOffer, getUserName } from '../utils';
import './prequal.style.scss';

const Prequal = ({
  prequalInvitationNumber,
  paymentEstimatorVersion,
  showApplyNow,
  isMobile,
}) => {
  const [prequalLink, setPrequalLink] = useState(
    DEFAULT_PREQUAL_URL
  );
  const [showModal, setShowModal] = useState(false);

  const openModal = () => {
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const analyticsInteraction = () => {
    clickEvent('plcc-check prequal', 'card');
  };

  const onClickNoThanks = () => {
    fetchDeclinePrequalOffer(prequalInvitationNumber, isMobile);
    showApplyNow();
  };

  const generateCardHeadingText = () => {
    const userFirstName = getUserName();
    const offerText = 'GET UP TO $100 OFF*';
    return !userFirstName
      ? offerText
      : `${userFirstName.toUpperCase()}, ${offerText}`;
  };

  // componentDidMount
  useEffect(() => {
    loadEstimatorScript(
      paymentEstimatorVersion,
      () => setPrequalLink(getPrequalOfferLink(isMobile, 'homepage', prequalInvitationNumber)),
      isMobile
    );
    impressionEvent('plcc-check prequal', 'card');
  }, []);

  const prequalMessage = (modal = false) => {
    return (
      <>
        <div className="prequal-flex">
          <div className="prequal-card-image-container">
            <Image
              src="https://assets.thdstatic.com/images/v1/plcc-angled-card.png"
              className="u--padding prequal-card-img"
              alt="Credit Card Icon"
              height="65"
              width="95"
            />
          </div>
          <div className="prequal-text">
            <h4 className="u__bold">You&apos;re Pre-Qualified!</h4>
            <p className="u--paddingTop u--paddingLarge-bottom prequal-offer-text-container">
              Get up to $100 OFF* your qualifying purchase and 6-month
              everyday financing** on purchases over $299.
              <br />
              <button
                className="u__default-link legal-click prequal-learn-more-link"
                type="button"
                onClick={openModal}
              >
                Learn More
              </button>
            </p>
          </div>
        </div>
        {modal ? (
          <div style={{ maxWidth: '250px', margin: '0 auto' }}>
            <Button tag="a" href={prequalLink} onClick={analyticsInteraction}>
              Apply Now
            </Button>
          </div>
        ) : (
          <div className="prequal-link-container u--paddingNormal-bottom flex-center">
            <div
              style={{
                float: 'left',
                minWidth: '70%',
                paddingRight: '25px',
                paddingTop: '25px',
                paddingBottom: '25px'
              }}
            >
              <div>
                <Button tag="a" href={prequalLink} onClick={analyticsInteraction} outline dark>
                  Apply Now
                </Button>
              </div>
            </div>
            <button
              className="u__default-link prequal-no-thanks-link"
              onClick={onClickNoThanks}
              type="button"
            >
              No Thanks
            </button>
          </div>
        )}
      </>
    );
  };

  const legalText = () => {
    return (
      <div className="prequal-legal-box">
        <p className="u--paddingTop u--paddingLeft u--paddingRight u--paddingBottom u__legal legal-text-line-spacing">
          <i>
            You can choose to stop receiving &quot;prescreened&quot; offers of credit from
            this and other companies by calling toll-free 1-888-5-OPTOUT
            (1-888-567-8688). See <u>PRESCREEN & OPT-OUT NOTICE</u> in the Terms
            and Conditions for more information about prescreened offers.
          </i>
        </p>
      </div>
    );
  };

  return (
    <>
      <CardTitle title={generateCardHeadingText()} />
      <CardContent>
        <Overlay
          modalClass="apply-now-overlay"
          closeButton
          isMobile={isMobile}
          open={showModal}
          onClose={closeModal}
        >
          <div className="apply-now-modal-message">
            <div className="u__text-align--center">
              <Image
                src={largeCardImage}
                className="u--padding apply-now-card-image"
                alt="Credit Card Icon"
                height="65"
                width="105"
              />
            </div>
            <h3 className="u__text-align--center u--paddingTop u--paddingBottom plcc-modal-title">
              {generateCardHeadingText()}
            </h3>
            <h3 className="u__text-align--center u--paddingBottom">
              You&apos;ve been pre-qualified for a Home Depot Consumer Card.
            </h3>
            <hr />
            <p className="u__text-align--center u--paddingLarge-top u--paddingBottom prequal-modal-text">
              Get <b>$25 off*</b> purchases of $25-$299 or <b>$50 off*</b>{' '}
              purchases of $300-$999 or <b>$100 off*</b> purchases of $1000 or
              more* on your next qualifying purchase when you open a new card.
              Plus, receive <b>6-months everyday financing**</b> on purchases
              over $299 when you use your Consumer Card.
            </p>
            <div style={{ maxWidth: '250px', margin: '0 auto', paddingTop: '10px' }}>
              <Button tag="a" href={prequalLink} onClick={analyticsInteraction}>
                Apply Now
              </Button>
            </div>
            <div className="u__text-align--center u--paddingTop u--paddingBottom">
              <button
                className="u__default-link prequal-no-thanks-link"
                onClick={onClickNoThanks}
                type="button"
              >
                No Thanks
              </button>
            </div>
            {legalText()}
          </div>
          <div className="u--padding">
            <div className="legal-border">
              <div className="u__flex">
                <p className="u--paddingTop u--paddingRight u--paddingLeft">*</p>
                <p className="u--paddingTop u__legal legal-text-line-spacing">
                  <b>
                    Offer is for new accounts, is subject to credit approval and
                    valid 1/30–7/29/2021.
                  </b>
                  <br />
                  This offer is redeemable for $25 off your purchase of $25-$299,
                  $50 off your purchase of $300-$999 or $100 off your purchase of
                  $1,000 or more on a single receipt within 30 days of your
                  account open date at The Home Depot® retail stores or
                  homedepot.com when made with your The Home Depot Consumer Credit
                  Card, The Home Depot Home Improver Card, The Home Depot
                  Commercial Revolving Charge or The Home Depot Commercial
                  Account. Valid in the U.S., U.S.V.I., Puerto Rico and Guam.
                  Offer does not apply to prior purchases, The Home Depot Gift
                  Cards or Certificates, third party gift cards, installation
                  products or services purchased in home, Tool Rental or to
                  Weber®, Bona®, Simplehuman®, Dacor®, Viking®, Fisher & Paykel®,
                  Sharp Insight™, Delonghi®, Buckhaven, Lynx®, Alfresco™, OCI,
                  Marvel®, Bertazzoni, Aga, Vent-A-Hood®, Samsung Chef Collection,
                  Bosch Benchmark® Series, Liebherr, Zephyr, Miele, Signature
                  Kitchen Suite, Jenn-Air®, Thermador®, Jeld-win Door Systems,
                  Monogram products and trade styles. May not be combined with
                  other discounts; ask an Associate for details.
                </p>
              </div>
              <div className="u__flex">
                <p className="u--paddingTop u--paddingRight u--paddingLeft">**</p>
                <p className="u--paddingTop u--paddingBottom u__bold u__legal legal-text-line-spacing">
                  With credit approval for qualifying purchases made on The Home
                  Depot or EXPO Design Center Consumer Credit Card. APR: 17.99% -
                  26.99%. Minimum interest charge: $2. See card agreement for
                  details including APR applicable to you. Offer valid for
                  consumer accounts in good standing; 6 months everyday credit
                  offer is subject to change without notice; see store for
                  details.
                </p>
              </div>
            </div>
          </div>
        </Overlay>
        <div className="prequal-card">
          {prequalMessage()}
          {legalText()}
        </div>
      </CardContent>
    </>
  );
};

Prequal.propTypes = {
  paymentEstimatorVersion: PropTypes.string,
  prequalInvitationNumber: PropTypes.string,
  showApplyNow: PropTypes.func,
  isMobile: PropTypes.bool
};
Prequal.defaultProps = {
  paymentEstimatorVersion: 'v1.0.36',
  prequalInvitationNumber: null,
  showApplyNow: null,
  isMobile: false,
};

export default Prequal;
